import { useCallback, useMemo, useSyncExternalStore } from 'react'
import { useAuth } from '@/components/auth/AuthContext'
import type { TutorialDef } from './types'

/**
 * Which page tutorials a person has watched or waved away, per signed-in
 * user and per tutorial version, so bumping a film's version surfaces its
 * peek tab once more. Like first-run state (firstRunStore.ts) this lives in
 * the browser until the account profile has a field for it.
 *
 * localStorage `tt_tutorials:<userId>` → { [id]: { seenVersion?, dismissedVersion? } }.
 * Every write broadcasts `tt:tutorials:changed` so the peek tab and the Help
 * drawer stay in step; other tabs follow through the `storage` event.
 */
export const TUTORIALS_CHANGED = 'tt:tutorials:changed'

export type TutorialProgress = Record<string, { seenVersion?: number; dismissedVersion?: number }>

const keyFor = (userId: string) => `tt_tutorials:${userId || 'anonymous'}`

/* When storage is unavailable (privacy mode, quota), progress still holds for
   this page's lifetime, so a dismissed peek tab doesn't bounce back. */
const memory = new Map<string, string>()
let storageBroken = false

function readRaw(key: string): string | null {
  if (!storageBroken) {
    try {
      return localStorage.getItem(key)
    } catch {
      storageBroken = true
    }
  }
  return memory.get(key) ?? null
}

function writeRaw(key: string, value: string | null) {
  if (value === null) memory.delete(key)
  else memory.set(key, value)
  try {
    if (value === null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch {
    storageBroken = true
  }
  window.dispatchEvent(new CustomEvent(TUTORIALS_CHANGED, { detail: { key } }))
}

function parse(raw: string | null): TutorialProgress {
  if (!raw) return {}
  try {
    const value = JSON.parse(raw) as unknown
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as TutorialProgress) : {}
  } catch {
    return {}
  }
}

function subscribe(onChange: () => void) {
  window.addEventListener(TUTORIALS_CHANGED, onChange)
  window.addEventListener('storage', onChange)
  return () => {
    window.removeEventListener(TUTORIALS_CHANGED, onChange)
    window.removeEventListener('storage', onChange)
  }
}

export function useTutorialProgress() {
  const { user } = useAuth()
  const key = keyFor(user?.userId ?? user?.id ?? '')
  const raw = useSyncExternalStore(
    subscribe,
    () => readRaw(key),
    () => null,
  )
  const progress = useMemo(() => parse(raw), [raw])

  const update = useCallback(
    (def: TutorialDef, patch: { seenVersion?: number; dismissedVersion?: number }) => {
      const current = parse(readRaw(key))
      writeRaw(key, JSON.stringify({ ...current, [def.id]: { ...current[def.id], ...patch } }))
    },
    [key],
  )

  const isUnwatched = useCallback(
    (def: TutorialDef) => {
      const entry = progress[def.id]
      return entry?.seenVersion !== def.version && entry?.dismissedVersion !== def.version
    },
    [progress],
  )
  const markSeen = useCallback((def: TutorialDef) => update(def, { seenVersion: def.version }), [update])
  const dismissPeek = useCallback((def: TutorialDef) => update(def, { dismissedVersion: def.version }), [update])
  const reset = useCallback(() => writeRaw(key, null), [key])

  return { isUnwatched, markSeen, dismissPeek, reset, progress }
}
